/**
 * The timeline page -- the whole tail, filterable.
 *
 * This is the replacement for the old ring buffer that lost history on every
 * restart: `ledger.tail` replays from any cursor, so what you see here is the
 * account, not a cache of the last thousand hook events.
 *
 * It has a second way in. A surface's card head asks 「查看执行过程」, and that
 * lands here narrowed to the one turn that work ran in (`panel/surfaceTrace.ts`
 * holds the address). Narrowed here and not on the server: `/api/timeline` has
 * no turn axis, and every row of a turn carries `turn_id`, so the narrowing is
 * a filter over the rows this page already holds. Arriving that way, the page
 * says what it is showing in words -- somebody followed a sentence about their
 * own work to get here, not a filter chip.
 */

import { Button, Drawer, Field, SearchSelect } from "../components/rumonai";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api, type LedgerEvent, type RecordPanel, type TimelineResponse } from "../lib/api";
import {
  formatTime,
  kindLabel,
  labelForState,
  ledgerLabel,
  summarizeRow,
} from "../lib/format";
import type { Tone } from "../lib/format";
import type { PanelBlock } from "../panel/spec";
import { BlockView } from "../panel/blocks";
import { recordClaimMatches } from "../panel/claims";
import { traceFromHash, type SurfaceTrace } from "../panel/surfaceTrace";
import { Clip, EmptyState, ErrorState, LoadingState, Scroller, StatusBadge } from "../shell/ui";

interface Filters {
  ledger: string[];
  kind: string[];
  /** A row ledger's state.  The verb lives in the row's status, not in the kind. */
  state: string[];
  session_id: string;
  principal_id: string;
  q: string;
}

const EMPTY: Filters = {
  ledger: [],
  kind: [],
  state: [],
  session_id: "",
  principal_id: "",
  q: "",
};

export function TimelinePage({
  version,
  recordPanels = [],
}: {
  version: number;
  recordPanels?: RecordPanel[];
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [body, setBody] = useState<TimelineResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [selected, setSelected] = useState<LedgerEvent | null>(null);
  // Read off the hash rather than taken as a prop: this page is reached by a
  // link somebody may bookmark or come back to, and a prop would mean the shell
  // had to carry a surface's turn through its own route table to hand it over.
  const [trace, setTrace] = useState<SurfaceTrace | null>(() => traceFromHash(location.hash));
  useEffect(() => {
    const onHash = () => setTrace(traceFromHash(location.hash));
    addEventListener("hashchange", onHash);
    return () => removeEventListener("hashchange", onHash);
  }, []);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    filters.ledger.forEach((v) => p.append("ledger", v));
    filters.kind.forEach((v) => p.append("kind", v));
    filters.state.forEach((v) => p.append("state", v));
    if (filters.session_id) p.set("session_id", filters.session_id);
    if (filters.principal_id) p.set("principal_id", filters.principal_id);
    if (filters.q) p.set("q", filters.q);
    p.set("limit", "400");
    return p;
  }, [filters]);

  const load = useCallback(() => {
    api
      .timeline(params)
      .then((next) => {
        setBody(next);
        setError(null);
      })
      .catch(setError);
  }, [params]);

  useEffect(load, [load, version]);

  const facets = body?.facets ?? {};
  const active =
    filters.ledger.length + filters.kind.length + filters.state.length > 0 ||
    Boolean(filters.session_id || filters.principal_id || filters.q) ||
    Boolean(trace);
  // The rows of one turn, when somebody followed 「查看执行过程」 to get here.
  const events = useMemo(
    () => trace
      ? (body?.events ?? []).filter(event => event.row?.turn_id === trace.turn)
      : body?.events ?? [],
    [body, trace],
  );
  const clear = () => {
    setFilters(EMPTY);
    if (trace) {
      setTrace(null);
      history.replaceState(null, "", "#/timeline");
    }
  };

  return (
    <div className="timeline-page">
      <header className="page-head">
        <h1 className="page-title" tabIndex={-1}>
          {trace ? "执行过程" : "时间线"}
        </h1>
        <p className="page-note micro">
          {trace
            ? `${trace.about ? `「${trace.about}」` : "这块界面"}上一次改动时做的事，最新的在上面。`
            : "全账本尾随。看到多少条取决于游标从哪里开始重放，不取决于进程活了多久。"}
        </p>
      </header>

      <div className="filters" role="group" aria-label="时间线过滤">
        <Chips
          label="账本"
          options={facets.ledger ?? []}
          selected={filters.ledger}
          render={ledgerLabel}
          onToggle={(value) =>
            setFilters((f) => ({ ...f, ledger: toggle(f.ledger, value) }))
          }
        />
        {/*
          The kind axis lists only L0 and registry kinds.  A row ledger's kind is
          its table name, which is the same axis as the ledger chips above; two
          identical rows of chips can also fight each other, so the server leaves
          them out of this facet.  The verb moved to the row's status, which
          makes it an axis of its own.
        */}
        <Chips
          label="类型"
          options={facets.kind ?? []}
          limit={14}
          selected={filters.kind}
          render={kindLabel}
          onToggle={(value) => setFilters((f) => ({ ...f, kind: toggle(f.kind, value) }))}
        />
        <Chips
          label="状态"
          options={facets.state ?? []}
          selected={filters.state}
          render={labelForState}
          onToggle={(value) => setFilters((f) => ({ ...f, state: toggle(f.state, value) }))}
        />
        <div className="filter-selects">
          <Field label="会话"><SearchSelect label="会话" value={filters.session_id}
            onValueChange={session_id => setFilters(f => ({ ...f, session_id }))}
            items={[{ value: "", label: "全部" }, ...(facets.session_id ?? []).map(value => ({ value, label: value }))]} /></Field>
          <Field label="身份"><SearchSelect label="身份" value={filters.principal_id}
            onValueChange={principal_id => setFilters(f => ({ ...f, principal_id }))}
            items={[{ value: "", label: "全部" }, ...(facets.principal_id ?? []).map(value => ({ value, label: value }))]} /></Field>
          <Field label="找字"><input type="search" value={filters.q} placeholder="正文里搜"
            onChange={event => setFilters(f => ({ ...f, q: event.target.value }))} /></Field>
          {active ? (
            <Button variant="ghost" size="small" type="button"  onClick={clear}>
              {trace ? "看全部" : "清空过滤"}
            </Button>
          ) : null}
        </div>
      </div>

      {error ? (
        <ErrorState error={error} onRetry={load} />
      ) : body === null ? (
        <LoadingState label="读时间线" />
      ) : events.length === 0 ? (
        <EmptyState
          title={trace ? "这次改动的记录不在这一段里" : active ? "这些条件下没有事件" : "账本里还没有事件"}
          hint={
            trace
              ? "这一页只回放最近的一段，更早的改动要往前翻。按「看全部」回到全部记录。"
              : active
                ? "换个条件，或者清空过滤看全部。"
                : "核跑起来、有人说话或有工具执行之后，事件会实时出现在这里。"
          }
        />
      ) : (
        <>
          <p className="micro list-meta">
            {trace ? `这次改动留下 ${events.length} 条记录` : (
              // Left as the four text nodes it always was: the same sentence in
              // one node lays out a fraction of a pixel differently, and this
              // page's screenshot is compared byte for byte.
              <>显示 {events.length} 条，本进程一共看过 {body.total_seen} 条</>
            )}
          </p>
          <Scroller label="时间线事件">
            <table className="pa-table table-timeline">
              <caption className="visually-hidden">账本事件，最新在上</caption>
              <thead>
                <tr>
                  <th scope="col">时间</th>
                  <th scope="col">账本</th>
                  <th scope="col">类型</th>
                  <th scope="col">摘要</th>
                  <th scope="col">会话 / 身份</th>
                  <th scope="col" className="num align-end">
                    游标
                  </th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => {
                  const panel = recordPanelFor(recordPanels, event);
                  const open = expanded === event.cursor;
                  return (
                    <Fragment key={event.cursor}>
                      <tr
                        tabIndex={0}
                        className={selected?.cursor === event.cursor ? "row-selected" : undefined}
                        onClick={() => setSelected(event)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelected(event);
                          }
                        }}
                      >
                        <td className="mono">{formatTime(event.ts)}</td>
                        <td>{ledgerLabel(event.ledger)}</td>
                        <td>
                          <StatusBadge
                            tone={toneForEvent(event.kind, event.row ?? {})}
                            label={kindLabel(event.kind)}
                          />
                        </td>
                        <td className="cell-summary">
                          <Clip text={summarizeRow(event.row ?? {})} />
                          {panel ? (
                            <Button variant="ghost" size="small"
                              type="button"
                              className="record-toggle"
                              aria-expanded={open}
                              data-testid={`record-toggle-${event.cursor}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpanded(open ? null : event.cursor);
                              }}
                            >
                              {open ? "收起" : panel.spec.title}
                            </Button>
                          ) : null}
                        </td>
                        <td className="mono cell-ids">
                          <Clip
                            text={`${event.session_id ?? "—"}${
                              event.principal_id ? ` · ${event.principal_id}` : ""
                            }`}
                          />
                        </td>
                        <td className="num align-end mono">{event.cursor}</td>
                      </tr>
                      {panel && open ? (
                        <tr className="record-row">
                          <td colSpan={6}>
                            <RecordCard panel={panel} event={event} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </Scroller>
        </>
      )}

      {selected ? (
        <Drawer open={Boolean(selected)} onOpenChange={open => { if (!open) setSelected(null); }}
          title={kindLabel(selected.kind)} description="事件详情" closeLabel="关闭" className="timeline-inspector">
          <dl className="kv">
            <div className="kv-row">
              <dt className="kv-key micro">游标</dt>
              <dd className="kv-value mono">{selected.cursor}</dd>
            </div>
            <div className="kv-row">
              <dt className="kv-key micro">插件</dt>
              <dd className="kv-value mono">{selected.plugin ?? "核"}</dd>
            </div>
            <div className="kv-row">
              <dt className="kv-key micro">时间</dt>
              <dd className="kv-value mono">{formatTime(selected.ts)}</dd>
            </div>
          </dl>
          <WholeRow event={selected} />
        </Drawer>
      ) : null}
    </div>
  );
}

/**
 * The selected event's row, all of it.
 *
 * The server keeps a model request without the context it was sent with and
 * says so (`whole: false`); the one opened here is read whole from the Agent
 * by its ref, and until it arrives nothing pretends the kept part is all.
 */
function WholeRow({ event }: { event: LedgerEvent }) {
  const partial = event.whole === false;
  const [read, setRead] = useState<{ ref: string; row?: Record<string, unknown>; error?: unknown } | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!partial) return;
    const controller = new AbortController();
    setRead(null);
    api
      .event(event.ref, controller.signal)
      .then((body) => {
        if (!controller.signal.aborted) setRead({ ref: event.ref, row: body.event.row });
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setRead({ ref: event.ref, error });
      });
    return () => controller.abort();
  }, [partial, event.ref, attempt]);

  if (!partial) return <pre className="mono args-dump">{JSON.stringify(event.row, null, 2)}</pre>;
  const mine = read?.ref === event.ref ? read : null;
  if (mine?.error) return <ErrorState error={mine.error} onRetry={() => setAttempt((n) => n + 1)} />;
  if (!mine?.row) return <LoadingState label="读整行" />;
  return (
    <pre className="mono args-dump" data-whole="true">
      {JSON.stringify(mine.row, null, 2)}
    </pre>
  );
}

function toggle(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function Chips({
  label,
  options,
  selected,
  onToggle,
  render,
  limit,
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
  render: (value: string) => string;
  limit?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  if (options.length === 0) return null;
  // What is selected is always listed, or a filter already in force would
  // disappear from the interface.
  const head =
    limit && !expanded
      ? options.filter((v, i) => i < limit || selected.includes(v))
      : options;
  const hidden = options.length - head.length;
  return (
    <div className="chips">
      <span className="micro chips-label">{label}</span>
      {head.map((value) => (
        <Button variant="ghost" size="small"
          key={value}
          type="button"
          className={`chip${selected.includes(value) ? " chip-on" : ""}`}
          aria-pressed={selected.includes(value)}
          onClick={() => onToggle(value)}
        >
          {render(value)}
        </Button>
      ))}
      {hidden > 0 ? (
        <Button variant="ghost" size="small"
          type="button"
          className="chip chip-more"
          onClick={() => setExpanded(true)}
        >
          还有 {hidden} 种
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The colour of one event, read off the event.
 *
 * There is nothing else left to read it off.  This used to be the verdict a
 * decision row carried -- `verdict`, `review_owed`, `decided_by` -- and §7.140
 * took all three off the record: the core knows only that a hold ended, and
 * whether a policy owed anybody a second look is that policy's own book.
 * §7.143 then took the row ledgers off the tail, so there is no status column
 * on any event either, and falling back to one meant every line on this page
 * was drawn in the hatch that means "outcome unknown" -- about events that are
 * perfectly well known.
 *
 * So each of these is a fact the event itself states, and everything else is
 * neutral: an event happened, which is not a state and does not want a colour.
 */
function toneForEvent(kind: string, row: Record<string, unknown>): Tone {
  if (kind === "l0.ingress_refused") return "critical";
  if (kind === "l0.effect.held") return "warning";
  if (kind === "l0.tool_result") {
    const result = row.result;
    if (result && typeof result === "object") {
      return (result as Record<string, unknown>).ok === false ? "critical" : "good";
    }
  }
  if (kind === "l0.turn_final") {
    const outcome = (row.result as Record<string, unknown> | undefined)?.outcome;
    return outcome === "failed" || outcome === "error" ? "critical" : "good";
  }
  return "neutral";
}

/**
 * Which record panel claims this event.
 *
 * `effect.succeeded` is an exact event kind; `producer_video.` (a prefix) and
 * `tool:producer_video.render` (one tool) are matched only against the row's
 * tool (`panel/claims.ts`).  First declaration wins.
 */
export function recordPanelFor(
  panels: RecordPanel[],
  event: LedgerEvent,
): RecordPanel | null {
  const tool = typeof event.row?.tool === "string" ? event.row.tool : null;
  for (const panel of panels) {
    for (const claim of panel.renders) {
      if (recordClaimMatches(claim, event.kind, tool)) return panel;
    }
  }
  return null;
}

/**
 * One event, drawn with the blocks the plugin that claims it declared.  Expanded
 * inline, without leaving the page.
 *
 * The data is the event itself, handed over as the panel's one read, so a
 * block's `body` addresses it the same way it would address a tool's answer:
 * `row.cost_cny` reads a field off the row, and so do `ts` and `kind`. A
 * record panel that went and fetched something else would no longer be
 * rendering *this* row, which is why nothing here consults its `reads`.
 */
function RecordCard({ panel, event }: { panel: RecordPanel; event: LedgerEvent }) {
  const reads = useMemo(
    () => [
      {
        type: "tail" as const,
        state: "ready" as const,
        value: event as unknown as Record<string, unknown>,
      },
    ],
    [event],
  );
  return (
    <section className="record-card" data-testid="record-card" data-panel={panel.key}>
      <header className="record-card-head">
        <span className="record-card-title">{panel.spec.title}</span>
        <span className="micro record-card-by">
          由 <code className="mono">{panel.plugin}</code> 自己声明
        </span>
      </header>
      <div className="record-card-blocks">
        {(panel.spec.blocks as PanelBlock[]).map((block, i) => (
          <BlockView
            key={i}
            block={block}
            index={i}
            reads={reads}
            busy={null}
            runAction={async () => {
              /* The record slot renders one event; actions belong to its own full panel */
              return "";
            }}
          />
        ))}
      </div>
    </section>
  );
}
